import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { Button } from 'components/common';
import { HomePageSettingsTilesQuickLinksAddOrEdit } from '.';

import type { HomePageModule } from 'shared-types';
import type { ModuleInSettings } from './HomePageSettingsModules';

type HomePageModuleLink = HomePageModule['data']['links'][0];

type Props = {
  module: ModuleInSettings;
  updateModule: (module: ModuleInSettings) => void;
};

const HomePageSettingsTilesQuickLinks = ({ module, updateModule }: Props) => {
  const { messages, theme } = useReduxState();

  const handleAddLink = (values: HomePageModuleLink) => {
    const modifiedModule = JSON.parse(JSON.stringify(module));
    modifiedModule.data = {
      links: [...(modifiedModule.data?.links || []), values]
    };
    updateModule(modifiedModule);
  };

  const handleEditLink = (linkIndex: number, values: HomePageModuleLink) => {
    const modifiedModule = JSON.parse(JSON.stringify(module));
    modifiedModule.data.links[linkIndex] = values;
    updateModule(modifiedModule);
  };

  const handleMoveLink = (linkIndex: number, increment: number) => {
    const modifiedModule = JSON.parse(JSON.stringify(module));
    const movedLink = modifiedModule.data.links.splice(linkIndex, 1)[0];
    modifiedModule.data.links.splice(linkIndex + increment, 0, movedLink);
    updateModule(modifiedModule);
  };

  const handleRemoveLink = (linkIndex: number) => {
    const modifiedModule = JSON.parse(JSON.stringify(module));
    modifiedModule.data.links.splice(linkIndex, 1);
    updateModule(modifiedModule);
  };

  return (
    <div>
      <div className="links">
        {module?.data?.links?.map((link, i: number) => (
          <div className="link" key={i}>
            <div className="content">
              <div className="inner">
                <div className="icon">
                  <FontAwesomeIcon icon="link" />
                </div>

                <div className="info">
                  <div className="title">{link.title}</div>
                  {link.description ? (
                    <div className="description">{link.description}</div>
                  ) : null}
                </div>
              </div>
              <div className="actions">
                <Button
                  category="lighter"
                  disabled={i === 0}
                  icon="arrow-left"
                  isRounded={true}
                  onClick={() => handleMoveLink(i, -1)}
                  size="small"
                  tooltip={messages.commonMove}
                />
                <Button
                  category="lighter"
                  disabled={i === module?.data?.links.length - 1}
                  icon="arrow-right"
                  isRounded={true}
                  onClick={() => handleMoveLink(i, 1)}
                  size="small"
                  tooltip={messages.commonMove}
                />
                <HomePageSettingsTilesQuickLinksAddOrEdit
                  buttonCategory="lighter"
                  buttonIcon="pen"
                  buttonProps={{
                    isRounded: true,
                    size: 'small',
                    tooltip: messages.commonEdit
                  }}
                  initialValues={{
                    description: link.description,
                    title: link.title,
                    url: link.url
                  }}
                  modalActionText={messages.commonSave}
                  modalTitle={messages.commonEdit}
                  onSubmit={(values) => handleEditLink(i, values)}
                />
                <Button
                  category="lighter"
                  icon="trash-alt"
                  isRounded={true}
                  onClick={() => handleRemoveLink(i)}
                  size="small"
                  tooltip={messages.commonRemove}
                />
              </div>
            </div>
          </div>
        ))}

        <div className="add">
          <HomePageSettingsTilesQuickLinksAddOrEdit
            buttonCategory="transparent"
            buttonIcon="plus"
            buttonStyle={{
              alignItems: 'center',
              border: `2px dashed ${theme.lightGreyDarkActive}`,
              borderRadius: theme.cardBorderRadius,
              display: 'flex',
              height: '100%',
              justifyContent: 'center',
              width: '100%'
            }}
            buttonText={messages.commonAddLink}
            modalActionText={messages.commonAdd}
            modalTitle={messages.commonAddLink}
            onSubmit={handleAddLink}
          />
        </div>
      </div>

      <style jsx>{`
        .links {
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          gap: 20px;
          padding: 20px;
        }
        .add,
        .link {
          background-color: white;
          border-radius: ${theme.cardBorderRadius};
          display: flex;
          outline: 0;
          transition: box-shadow 0.3s;
          width: 100%;
          min-height: 100px;
          min-width: 100px;
        }
        .add :global(> button) {
          background-color: ${theme.lightGreyLighter};
          color: ${theme.textColorLighter};
        }
        .add :global(> button:hover) {
          background-color: white;
          color: ${theme.textColor};
        }
        .content {
          color: ${theme.textColor};
          display: flex;
          flex-direction: column;
          flex-grow: 1;
          font-size: 1.5rem;
          font-weight: 600;
          gap: 10px;
          min-height: 100px;
          justify-content: space-between;
          padding: 10px;
        }
        .icon {
          align-items: center;
          background-color: ${theme.lightGrey};
          border-radius: 32px;
          color: ${theme.textColorLight};
          display: flex;
          flex-grow: 0;
          flex-shrink: 0;
          font-size: 1rem;
          justify-content: center;
          height: 24px;
          width: 24px;
        }
        .inner {
          display: flex;
          flex-direction: row-reverse;
          gap: 7px;
        }
        .info {
          display: flex;
          flex-direction: column;
          flex-grow: 1;
          flex-shrink: 1;
          gap: 3px;
          justify-content: center;
        }
        .title {
          color: ${theme.textColor};
          display: -webkit-box;
          font-size: 1.4rem;
          font-weight: 600;
          overflow: hidden;
          text-overflow: ellipsis;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 3;
        }
        .description {
          color: ${theme.textColorLight};
          font-size: 1.2rem;
          font-weight: 400;
        }
        .actions {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          justify-content: flex-end;
        }
        @media (min-width: ${breakpoints.small}px) {
          .add,
          .link {
            width: calc(50% - 10px);
          }
        }
        @media (min-width: ${breakpoints.medium}px) {
          .add,
          .link {
            width: calc(33.333% - 13.333px);
          }
        }
        @media (min-width: ${breakpoints.large}px) {
          .add,
          .link {
            width: calc(25% - 15px);
          }
        }
      `}</style>
    </div>
  );
};

export default HomePageSettingsTilesQuickLinks;
